'use client';

import { isDevEnv } from '@/lib/utils';
import {
    BaseServerResponse,
    PageServerResponse,
} from '@/server/base/base-trpc-service';
import { TRPCRequestOptions } from '@trpc/client';
import { RowDataUpdatedEvent } from 'ag-grid-community';
import {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import TableAG from './table-ag';
import { RowSelectedEvent, TableAGProps, TableAGRef } from './table-ag-types';

export type CallbackTableAGV2 = (rest: {
    trpcOpts?: TRPCRequestOptions;
}) => Promise<BaseServerResponse<PageServerResponse<any>> | undefined>;

export interface TableAGV2Ref extends TableAGRef {
    onReload: VoidFunction;
}

export interface TableAGV2Props extends TableAGProps {
    onLoadTableData?: CallbackTableAGV2;

    /**callback setState rowSelected */
    setRowSelected?: (rowSelected: any) => void;

    /**
     *
     */
}

/**
 * Đây vẫn sử dụng core tableAG, nhưng sẽ được custom nhận vào là 1 hàm callback xử lý data và loading ở phía trong component
 * trả về PageResponse chứ không nhận data trực tiếp từ useQuery
 * @description Nên sử dụng khi sử dụng apiUtils để call API
 * @returns ReactNode
 */
const TableAGV2 = forwardRef<TableAGV2Ref, TableAGV2Props>((props, ref) => {
    const {
        onLoadTableData,
        setRowSelected,

        onRowDataUpdated: passOnRowDataUpdated,
        onRowSelected: passOnRowSelected,
        ...tableAGProps
    } = props;

    const _extendRef = useRef<TableAGRef>(null);
    const controllerRef = useRef<AbortController | null>(null);

    const [isFetching, setIsFetching] = useState(true);
    const [queryData, setQueryData] =
        useState<BaseServerResponse<PageServerResponse<any>>>();

    /**Handle call API, loading table */
    const handleFetchData = useCallback(
        async (controller?: AbortController) => {
            setIsFetching(true);

            let queryFn:
                | undefined
                | BaseServerResponse<PageServerResponse<any>>;
            if (onLoadTableData) {
                try {
                    queryFn = await onLoadTableData({
                        trpcOpts: { signal: controller?.signal },
                    });
                } catch (error) {
                    if (isDevEnv()) {
                        console.info('Fetch table data warn: ', error);
                    }
                }
            }

            if (typeof queryFn !== 'undefined') {
                setIsFetching(false);
            }
            setQueryData(queryFn);
        },
        [onLoadTableData]
    );

    /**Callback reload table */
    const onReloadTable = useCallback(() => {
        handleFetchData();
    }, [handleFetchData]);

    /**
     * Event reset rowData
     * @description Sử dụng khi có dòng selected thì dữ liệu trên bảng reload lại
     * @param {any} rowData
     */
    const onRowDataUpdated = useCallback(
        (e: RowDataUpdatedEvent<any, any>) => {
            setRowSelected?.([]);
            passOnRowDataUpdated?.(e);
        },
        [passOnRowDataUpdated, setRowSelected]
    );

    /**Update row selected */
    const onRowSelected = useCallback(
        (e: RowSelectedEvent<any, any>) => {
            const rowData = e.api.getSelectedRows() ?? [];
            setRowSelected?.(rowData);
            passOnRowSelected?.(e);
        },
        [passOnRowSelected, setRowSelected]
    );

    /**
     * Side effect call API of table AG
     */
    useEffect(() => {
        // cancel request cũ nếu còn chạy
        controllerRef.current?.abort();

        const controller = new AbortController();
        controllerRef.current = controller;

        handleFetchData(controllerRef.current);
    }, [handleFetchData]);

    const isPending = !!(tableAGProps?.isLoading || isFetching);

    useImperativeHandle(ref, () => {
        return { onReload: onReloadTable, ...(_extendRef.current ?? {}) };
    }, [onReloadTable]);

    return (
        <>
            <TableAG
                ref={_extendRef}
                onRowSelected={onRowSelected}
                onRowDataUpdated={onRowDataUpdated}
                {...tableAGProps}
                isLoading={isPending}
                queryData={isPending ? undefined : queryData}
            />
        </>
    );
});

TableAGV2.displayName = 'TableAGV2';
export default memo(TableAGV2);
